import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { createInvoice, getOrdersByRestaurant } from '../../services/api';
import { formatCurrency } from '../../utils/formatCurrency';
import { getDateLocale } from '../../utils/dateLocale';
import { storage } from '../../utils/storage';
import './InvoicingModals.css';

const RECEPTOR_DOC_TYPES = [
  { value: 99, label: 'Consumidor Final (sin identificar)' },
  { value: 80, label: 'CUIT' },
  { value: 96, label: 'DNI' },
  { value: 86, label: 'CUIL' },
];

const CUIT_WEIGHTS = [5, 4, 3, 2, 7, 6, 5, 4, 3, 2];

const validateCuitCheckDigit = (digits) => {
  if (digits.length !== 11) return false;
  const nums = digits.split('').map(Number);
  const sum = CUIT_WEIGHTS.reduce((acc, w, i) => acc + w * nums[i], 0);
  const remainder = sum % 11;
  const expected = remainder === 0 ? 0 : remainder === 1 ? 9 : 11 - remainder;
  return nums[10] === expected;
};

const formatCuit = (raw) => {
  const digits = raw.replace(/\D/g, '').slice(0, 11);
  if (digits.length <= 2) return digits;
  if (digits.length <= 10) return `${digits.slice(0, 2)}-${digits.slice(2)}`;
  return `${digits.slice(0, 2)}-${digits.slice(2, 10)}-${digits.slice(10)}`;
};

const rawDigits = (formatted) => formatted.replace(/\D/g, '');

const RECEPTOR_CONDICION_IVA_VALUES = [5, 1, 6, 4];

const CreateInvoiceModal = ({ onClose, onCreated, preselectedOrderId }) => {
  const { t, i18n } = useTranslation();
  const [step, setStep] = useState(1); // 1: select orders, 2: receptor data, 3: confirm
  const [orders, setOrders] = useState([]);
  const [selectedOrders, setSelectedOrders] = useState(
    preselectedOrderId ? [preselectedOrderId] : []
  );
  const [loadingOrders, setLoadingOrders] = useState(true);
  const [receptor, setReceptor] = useState({
    docTipo: 99,
    docNro: 0,
    condicionIVA: 5,
    nombre: 'Consumidor Final',
  });
  const [sendEmail, setSendEmail] = useState(false);
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});

  const restaurantId = storage.get('selectedRestaurantId');

  useEffect(() => {
    const loadOrders = async () => {
      try {
        const data = await getOrdersByRestaurant(restaurantId);
        // Filter paid, not-yet-invoiced orders
        const paidOrders = (data.orders || data.data || data || []).filter(
          (o) => o.paymentStatus === 'paid' && !o.invoiceId
        );
        setOrders(paidOrders);
      } catch (err) {
        console.error('Error loading orders:', err);
      } finally {
        setLoadingOrders(false);
      }
    };
    loadOrders();
  }, [restaurantId]);

  const toggleOrder = (orderId) => {
    setSelectedOrders((prev) =>
      prev.includes(orderId) ? prev.filter((id) => id !== orderId) : [...prev, orderId]
    );
  };

  const selectedTotal = orders
    .filter((o) => selectedOrders.includes(o.orderId))
    .reduce((sum, o) => sum + (o.total || 0), 0);

  const isCuitOrCuil = receptor.docTipo === 80 || receptor.docTipo === 86;

  const validateReceptor = () => {
    const errors = {};
    if (receptor.condicionIVA === 5) return errors;

    const digits = rawDigits(String(receptor.docNro || ''));
    if (!digits) {
      errors.docNro = t('invoicing.docNumberRequired', 'El número de documento es requerido');
    } else if (isCuitOrCuil) {
      if (digits.length !== 11) {
        errors.docNro = t(
          'invoicing.cuitInvalid',
          'CUIT inválido. Debe tener 11 dígitos con formato XX-XXXXXXXX-X'
        );
      } else if (!validateCuitCheckDigit(digits)) {
        errors.docNro = t(
          'invoicing.cuitCheckDigit',
          'El dígito verificador del CUIT es incorrecto'
        );
      }
    }

    if (!receptor.nombre?.trim()) {
      errors.nombre = t('invoicing.nameRequired', 'El nombre / razón social es requerido');
    }

    return errors;
  };

  const handleNextToConfirm = () => {
    const errors = validateReceptor();
    setFieldErrors(errors);
    if (Object.keys(errors).length === 0) {
      setStep(3);
    }
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      setError(null);
      const data = {
        restaurantId,
        orderIds: selectedOrders,
        receptor,
        sendEmail,
        email: sendEmail ? email : undefined,
      };
      const res = await createInvoice(data);
      setResult(res.data);
    } catch (err) {
      setError(
        err.response?.data?.afipError ||
          err.response?.data?.error ||
          t('invoicing.createError', 'Error al crear factura')
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-content--wide" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{t('invoicing.createInvoice', 'Crear Factura')}</h2>
          <button type="button" className="btn btn-ghost btn-sm" onClick={onClose}>
            <span className="material-icons">close</span>
          </button>
        </div>

        <div className="modal-body">
          {/* Step indicator */}
          <div className="invoice-steps">
            <div className={`invoice-step ${step >= 1 ? 'active' : ''}`}>
              <span className="step-number">1</span>
              <span>{t('invoicing.selectOrders', 'Pedidos')}</span>
            </div>
            <div className="step-connector" />
            <div className={`invoice-step ${step >= 2 ? 'active' : ''}`}>
              <span className="step-number">2</span>
              <span>{t('invoicing.receptorData', 'Receptor')}</span>
            </div>
            <div className="step-connector" />
            <div className={`invoice-step ${step >= 3 ? 'active' : ''}`}>
              <span className="step-number">3</span>
              <span>
                {step === 3 && result
                  ? t('invoicing.done', 'Listo')
                  : t('invoicing.confirm', 'Confirmar')}
              </span>
            </div>
          </div>

          {error && (
            <div className="invoice-error" role="alert" aria-live="assertive">
              <span className="material-icons">error</span>
              {error}
            </div>
          )}

          {/* Step 1: Select Orders */}
          {step === 1 && (
            <div className="invoice-step-content">
              <p className="invoice-step-desc">
                {t('invoicing.selectOrdersDesc', 'Seleccione los pedidos pagados a facturar:')}
              </p>
              {loadingOrders ? (
                <div className="invoice-loading">{t('common.loading', 'Cargando...')}</div>
              ) : orders.length === 0 ? (
                <div className="invoice-empty">
                  {t('invoicing.noPaidOrders', 'No hay pedidos pagados sin facturar')}
                </div>
              ) : (
                <div className="invoice-orders-list">
                  {orders.slice(0, 50).map((order) => (
                    <label
                      key={order.orderId}
                      className={`invoice-order-item ${selectedOrders.includes(order.orderId) ? 'selected' : ''}`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedOrders.includes(order.orderId)}
                        onChange={() => toggleOrder(order.orderId)}
                      />
                      <div className="invoice-order-info">
                        <span className="invoice-order-id">
                          {order.tableNumber
                            ? `Mesa ${order.tableNumber}`
                            : order.orderId.slice(-8)}
                        </span>
                        <span className="invoice-order-date">
                          {new Date(order.createdAt).toLocaleString(getDateLocale(i18n.language))}
                        </span>
                      </div>
                      <span className="invoice-order-total">
                        {formatCurrency(order.total || 0)}
                      </span>
                    </label>
                  ))}
                </div>
              )}
              {selectedOrders.length > 0 ? (
                <div className="invoice-selection-total">
                  <span>
                    {selectedOrders.length}{' '}
                    {t('invoicing.ordersSelected', 'pedido(s) seleccionado(s)')}
                  </span>
                  <span className="invoice-total-amount">
                    Total: {formatCurrency(selectedTotal)}
                  </span>
                </div>
              ) : (
                !loadingOrders &&
                orders.length > 0 && (
                  <div className="invoice-validation-msg" role="alert">
                    <span className="material-icons">info</span>
                    {t(
                      'invoicing.selectAtLeastOne',
                      'Seleccione al menos un pedido para continuar'
                    )}
                  </div>
                )
              )}
            </div>
          )}

          {/* Step 2: Receptor Data */}
          {step === 2 && (
            <div className="invoice-step-content">
              <p className="invoice-step-desc">
                {t('invoicing.receptorDesc', 'Datos del receptor de la factura:')}
              </p>
              <div className="invoice-form-grid">
                <div className="invoice-form-group">
                  <label>{t('invoicing.receptorCondicion', 'Condición IVA')}</label>
                  <select
                    value={receptor.condicionIVA}
                    onChange={(e) => {
                      const val = parseInt(e.target.value, 10);
                      setReceptor({
                        ...receptor,
                        condicionIVA: val,
                        docTipo: val === 5 ? 99 : 80,
                        docNro: val === 5 ? 0 : receptor.docNro,
                        nombre: val === 5 ? 'Consumidor Final' : '',
                      });
                    }}
                  >
                    {RECEPTOR_CONDICION_IVA_VALUES.map((val) => (
                      <option key={val} value={val}>
                        {t(`fiscal.ivaCondicion_${val}`)}
                      </option>
                    ))}
                  </select>
                </div>

                {receptor.condicionIVA !== 5 && (
                  <>
                    <div className="invoice-form-group">
                      <label>{t('invoicing.docType', 'Tipo Documento')}</label>
                      <select
                        value={receptor.docTipo}
                        onChange={(e) =>
                          setReceptor({ ...receptor, docTipo: parseInt(e.target.value, 10) })
                        }
                      >
                        {RECEPTOR_DOC_TYPES.filter((d) => d.value !== 99).map((d) => (
                          <option key={d.value} value={d.value}>
                            {d.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="invoice-form-group">
                      <label>{t('invoicing.docNumber', 'Número Documento')}</label>
                      <input
                        type="text"
                        value={
                          isCuitOrCuil
                            ? formatCuit(String(receptor.docNro || ''))
                            : receptor.docNro || ''
                        }
                        onChange={(e) => {
                          const value = e.target.value;
                          if (isCuitOrCuil) {
                            const digits = rawDigits(value).slice(0, 11);
                            setReceptor({ ...receptor, docNro: digits });
                          } else {
                            setReceptor({ ...receptor, docNro: value.replace(/\D/g, '') });
                          }
                          if (fieldErrors.docNro) {
                            setFieldErrors((prev) => ({ ...prev, docNro: undefined }));
                          }
                        }}
                        maxLength={isCuitOrCuil ? 13 : undefined}
                        placeholder={
                          isCuitOrCuil ? '##-########-#' : t('invoicing.docNumber', 'Número')
                        }
                        inputMode="numeric"
                      />
                      {fieldErrors.docNro && (
                        <span className="invoice-field-error" role="alert">
                          {fieldErrors.docNro}
                        </span>
                      )}
                    </div>

                    <div className="invoice-form-group">
                      <label>{t('invoicing.receptorName', 'Nombre / Razón Social')}</label>
                      <input
                        type="text"
                        value={receptor.nombre}
                        onChange={(e) => {
                          setReceptor({ ...receptor, nombre: e.target.value });
                          if (fieldErrors.nombre) {
                            setFieldErrors((prev) => ({ ...prev, nombre: undefined }));
                          }
                        }}
                        placeholder="Nombre del receptor"
                      />
                      {fieldErrors.nombre && (
                        <span className="invoice-field-error" role="alert">
                          {fieldErrors.nombre}
                        </span>
                      )}
                    </div>
                  </>
                )}

                <div className="invoice-form-group invoice-email-group">
                  <label className="invoice-checkbox-label">
                    <input
                      type="checkbox"
                      checked={sendEmail}
                      onChange={(e) => setSendEmail(e.target.checked)}
                    />
                    {t('invoicing.sendEmail', 'Enviar por email')}
                  </label>
                  {sendEmail && (
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="email@ejemplo.com"
                    />
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Confirmation / Result */}
          {step === 3 && !result && (
            <div className="invoice-step-content">
              <div className="invoice-confirm-warning">
                <span className="material-icons">warning</span>
                <p>
                  {t(
                    'invoicing.confirmWarning',
                    'Esta acción emitirá una factura fiscal ante AFIP y es irreversible. Verifique los datos antes de continuar.'
                  )}
                </p>
              </div>
              <div className="invoice-result-details">
                <div className="result-row">
                  <span>{t('invoicing.ordersSelected', 'Pedidos seleccionados')}:</span>
                  <span>{selectedOrders.length}</span>
                </div>
                <div className="result-row">
                  <span>{t('common.total', 'Total')}:</span>
                  <span>{formatCurrency(selectedTotal)}</span>
                </div>
                <div className="result-row">
                  <span>{t('invoicing.receptorCondicion', 'Condición IVA')}:</span>
                  <span>{t(`fiscal.ivaCondicion_${receptor.condicionIVA}`, '-')}</span>
                </div>
                {receptor.condicionIVA !== 5 && (
                  <>
                    <div className="result-row">
                      <span>{t('invoicing.docNumber', 'Documento')}:</span>
                      <span>
                        {isCuitOrCuil
                          ? formatCuit(String(receptor.docNro || ''))
                          : receptor.docNro || '-'}
                      </span>
                    </div>
                    <div className="result-row">
                      <span>{t('invoicing.receptorName', 'Nombre / Razón Social')}:</span>
                      <span>{receptor.nombre || '-'}</span>
                    </div>
                  </>
                )}
                {sendEmail && (
                  <div className="result-row">
                    <span>Email:</span>
                    <span>{email || '-'}</span>
                  </div>
                )}
              </div>
            </div>
          )}
          {step === 3 && result && (
            <div className="invoice-step-content invoice-result" role="status" aria-live="polite">
              <div className="invoice-result-icon">
                <span className="material-icons">check_circle</span>
              </div>
              <h3>{t('invoicing.invoiceCreated', 'Factura emitida exitosamente')}</h3>
              <div className="invoice-result-details">
                <div className="result-row">
                  <span>Tipo:</span>
                  <span>Factura {result.invoiceClass}</span>
                </div>
                <div className="result-row">
                  <span>Número:</span>
                  <span className="mono">
                    {String(result.puntoDeVenta).padStart(5, '0')}-
                    {String(result.numeroComprobante).padStart(8, '0')}
                  </span>
                </div>
                <div className="result-row">
                  <span>CAE:</span>
                  <span className="mono">{result.cae}</span>
                </div>
                <div className="result-row">
                  <span>Importe:</span>
                  <span>{formatCurrency(result.importeTotal || 0)}</span>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="modal-actions">
          {step === 1 && (
            <>
              <button type="button" className="btn btn-ghost" onClick={onClose}>
                {t('common.cancel', 'Cancelar')}
              </button>
              <button
                type="button"
                className="btn btn-primary"
                disabled={selectedOrders.length === 0}
                onClick={() => setStep(2)}
              >
                {t('common.next', 'Siguiente')}
              </button>
            </>
          )}
          {step === 2 && (
            <>
              <button type="button" className="btn btn-ghost" onClick={() => setStep(1)}>
                {t('common.back', 'Volver')}
              </button>
              <button type="button" className="btn btn-primary" onClick={handleNextToConfirm}>
                {t('common.next', 'Siguiente')}
              </button>
            </>
          )}
          {step === 3 && !result && (
            <>
              <button type="button" className="btn btn-ghost" onClick={() => setStep(2)}>
                {t('common.back', 'Volver')}
              </button>
              <button
                type="button"
                className="btn btn-primary"
                disabled={submitting}
                onClick={handleSubmit}
              >
                {submitting
                  ? t('invoicing.creating', 'Emitiendo...')
                  : t('invoicing.emit', 'Emitir Factura')}
              </button>
            </>
          )}
          {step === 3 && result && (
            <button type="button" className="btn btn-primary" onClick={onCreated}>
              {t('common.close', 'Cerrar')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreateInvoiceModal;
